import { childItemsFacet } from '@ms/items-view/lib/private/facets/SetFacets';
import {
  IItemCacheDispatch
} from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { resolveItems } from '@ms/items-view/lib/private/operations/getItems';
import { readItem } from '@ms/items-view/lib/private/operations/readItem';
import {
  folderPathFacet,
  listFacet,
  spDataSourceConfigurationFacet,
  spPageContextFacet,
  contentTypeKeysFacet,
  spListContentTypeFacet,
  largeFolderFacet,
  spPrefetchDataConfigurationFacet,
  serverViewFacet,
  currentDerivedViewFacet,
  showViewModifiedIndicatorFacet,
  contentTypeIdFacet,
  spClientSideFrameworkConfigurationFacet,
  gridInitInfoFacet,
} from './Facets';
import { IItemCacheThunkAction } from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import {
  spPinnedItemSetKeyFacet,
  spViewKeyFacet,
  spGridInitInfoKeyFacet,
} from './Keys';
import { ISPGetItemContext } from './interfaces/IGetServerData';
import { IFeature } from '@ms/odsp-utilities/lib/features/FeaturesBasic';
import { SimpleUri } from '@ms/items-view-utilities/lib/uri/SimpleUri';
import ListFilterUtilities from '@ms/odsp-datasources/lib/utilities/list/ListFilterUtilities';
import * as CamlUtilities from '@ms/odsp-datasources/lib/utilities/caml/CamlUtilities';
import * as CamlSerialization from '@ms/odsp-datasources/lib/utilities/caml/CamlSerialization';
import { IFilter } from '@ms/odsp-datasources/lib/interfaces/view/IViewArrangeInfo';
import { ItemUrlHelperBasic } from '@ms/odsp-datasources/lib/utilities/url/ItemUrlHelperBasic';
export { spDataSourceConfigurationFacet };
export { spPrefetchDataConfigurationFacet };
import { isDocumentLibrary, ListTemplateType } from '@ms/odsp-datasources/lib/dataSources/listCollection/ListTemplateType';
import { getViewXml, getTypeFilterViewXml } from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/ListItemPostDataHelpers';
import { ISPContentType } from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/interfaces/ISPGetItemResponse';
import { localeFacet } from '@ms/items-view/lib/private/facets/ConfigurationFacets';
import {
  spItemKeyFacet,
  spFolderItemSetKeyFacet,
  spListKeyFacet,
  spPageContextKeyFacet,
  needsAttentionViewId,
  spSearchItemSetKeyFacet
} from './Keys';
import { getFeatureChecker } from '../models/features/getFeatureChecker';
import { readItems } from '@ms/items-view/lib/private/operations/readItems';
import { IItemCache } from '@ms/items-view/lib/private/models/IItemCacheObserver';
import { readConfiguration } from '@ms/items-view/lib/private/operations/configuration/readConfiguration';
import Guid from '@ms/odsp-utilities/lib/guid/Guid';
import {
  IItemTypeFilterInput, ItemTypeFilter
} from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/interfaces/IItemTypeFilter';
import { FileTypeIconMap } from '@uifabric/file-type-icons/lib/FileTypeIconMap';

export interface IConstructGetItemSetContextProps {
  itemSetKey: typeof spFolderItemSetKeyFacet.type | typeof spSearchItemSetKeyFacet.type;
  contentTypes: ISPContentType[] | undefined;
  qosName: string;
  pageToken?: string;
  fetchNextGroupXml?: string;
  skipCache?: boolean;
}

export function constructGetItemSetContext(
  props: IConstructGetItemSetContextProps
): IItemCacheThunkAction<Promise<ISPGetItemContext>> {
  const {
    itemSetKey,
    contentTypes,
    pageToken,
    fetchNextGroupXml,
    qosName,
    skipCache = false
  } = props;

  return async (dispatch: IItemCacheDispatch) => {
    const itemKey = itemSetKey;

    const {
      disableGrouping
    } = itemKey;

    const parentFolderItemKey = spItemKeyFacet.serialize(itemKey);

    const folderFacets = dispatch(readItem({
      folderPath: folderPathFacet,
    }, parentFolderItemKey));
    const isLargeFolderFacet = dispatch(readItem({
      isLarge: largeFolderFacet
    }, parentFolderItemKey));

    const folderPath = (folderFacets && folderFacets.folderPath) || itemKey.rootFolder;
    const isLargeFolder = isLargeFolderFacet && isLargeFolderFacet.isLarge;

    const parentFolderPath = folderPath || new SimpleUri(itemKey.listFullUrl).path;

    const spPageContextFacets = dispatch(readItem({
      pageContext: spPageContextFacet
    }, spPageContextKeyFacet.serialize({
      webAbsoluteUrl: itemKey.webAbsoluteUrl,
      listFullUrl: itemKey.listFullUrl
    })));

    const getItemContext: ISPGetItemContext = {
      webAbsoluteUrl: itemKey.webAbsoluteUrl,
      listFullUrl: itemKey.listFullUrl,
      currentPageContext: spPageContextFacets && spPageContextFacets.pageContext,
      qosName: qosName,
      pageToken: pageToken,
      fetchNextGroupXml: fetchNextGroupXml,
      rootFolder: parentFolderPath,
      filterParams: itemSetKey.filterParams,
      sortField: itemSetKey.sortFieldKey,
      sortDir: itemSetKey.sortAscending ? 'Asc' : 'Desc',
      groupByField: itemSetKey.groupByFieldKey,
      searchTerm: 'searchTerm' in itemSetKey ? itemSetKey.searchTerm : undefined,
      viewPath: itemSetKey.viewPath,
      isLargeFolder: isLargeFolder,
      skipServiceWorkerCache: skipCache
    };

    // only need view metadata when the view is not exist in the cache or the view is not resolved.
    let needsViewMetadata = true;
    if (itemSetKey.viewKey) {
      if (itemSetKey.viewKey.viewId === needsAttentionViewId) {
        const {
          getNeedsAttentionView
        } = await import(/* webpackChunkName: 'addon-sp-needsattentionviewhelper' */'./needsAttentionViewHelper');
        const needsAttentionView = getNeedsAttentionView(contentTypes, itemSetKey, dispatch);
        const serializedViewKey = spViewKeyFacet.serialize(itemSetKey.viewKey);
        getItemContext.viewXml = needsAttentionView.getEffectiveViewXml();
        getItemContext.qosName = 'getNeedsAttentionViewItemSet';
        dispatch(resolveItems({
          items: {
            [serializedViewKey]: {
              ...currentDerivedViewFacet.pack(needsAttentionView),
              ...showViewModifiedIndicatorFacet.pack(false)
            }
          }
        }));
        needsViewMetadata = false;
      } else {
        getItemContext.viewId = itemSetKey.viewKey.viewId;

        const spViewKey = spViewKeyFacet.serialize(itemSetKey.viewKey);

        const {
          view,
          currentDerivedView,
          isServerViewResolved
        } = dispatch(readItems((itemCache: IItemCache) => {
          return {
            view: itemCache.demandItemFacet(serverViewFacet, spViewKey),
            currentDerivedView: itemCache.demandItemFacet(currentDerivedViewFacet, spViewKey),
            isServerViewResolved: itemCache.isItemFacetResolved(serverViewFacet, spViewKey)
          };
        }));

        if (view && isServerViewResolved) {
          needsViewMetadata = false;
        }

        if (currentDerivedView && currentDerivedView.isDirty()) {
          // If view has been modified, use the XML to get data.
          getItemContext.viewXml = currentDerivedView.getEffectiveViewXml();
        }
      }
    }

    if (itemSetKey.filterParams) {
      const filterList = ListFilterUtilities.getFilterList(itemSetKey.filterParams, undefined, true, true);
      if (shouldConvertFiltersToCaml(filterList)) {
        // When filter params contains today format filter or use lookup id filter,
        // we need to use filter xml instead of filter params.
        getItemContext.additionalFiltersXml = CamlSerialization.filtersToCaml(filterList);
        getItemContext.filterParams = undefined;
      }
    }

    if (itemSetKey.extFilters && itemSetKey.extFilters.length > 0) {
      const filters: IItemTypeFilterInput[] = [];

      for (const filter of itemSetKey.extFilters) {
        if (filter === 'folder') {
          filters.push(ItemTypeFilter.folder);
        } else if (filter === 'media') {
          filters.push(ItemTypeFilter.media);
        } else {
          filters.push(filter);
        }
      }

      getItemContext.additionalFiltersXml = CamlSerialization.filtersToCaml([
        ...(getItemContext.additionalFiltersXml ? [getItemContext.additionalFiltersXml] : []),
        getTypeFilterViewXml({ filters }, { fileTypeIconMap: FileTypeIconMap })
      ], 'And');
    }

    const spListKey = spListKeyFacet.serialize(itemKey);
    const gridInitInfoKey = spGridInitInfoKeyFacet.serialize({
      listKey: {
        webAbsoluteUrl: itemSetKey.webAbsoluteUrl,
        listFullUrl: itemSetKey.listFullUrl
      },
      viewKey: itemSetKey.viewKey
    });

    const {
      listFacets,
      contentTypeKeys,
      isContentTypeKeysResolved,
      isSpotlightResolved,
      gridInitInfo,
      isGridInitInfoResolved
    } = dispatch(readItems((itemCache: IItemCache) => {
      return {
        listFacets: itemCache.demandItemFacet(listFacet, spListKey),
        contentTypeKeys: itemCache.demandItemFacet(contentTypeKeysFacet, spListKey),
        isContentTypeKeysResolved: itemCache.isItemFacetResolved(contentTypeKeysFacet, spListKey),
        isSpotlightResolved: itemCache.isItemFacetResolved(childItemsFacet, spPinnedItemSetKeyFacet.serialize({
          parentItemKey: itemKey,
          viewKey: itemSetKey.viewKey
        })),
        gridInitInfo: itemCache.demandItemFacet(gridInitInfoFacet, gridInitInfoKey),
        isGridInitInfoResolved: itemCache.isItemFacetResolved(gridInitInfoFacet, gridInitInfoKey)
      };
    }));

    const DocumentSetsModernView = { ODB: 1114, Fallback: false };
    const UseItemsScope = { ODB: 851 };
    const currentPageContext = getItemContext.currentPageContext;
    const expFeatures = (currentPageContext && currentPageContext.ExpFeatures) || [];
    const isFeatureEnabled = dispatch(readItems((itemCache: IItemCache) => getIsFeatureEnabled(itemCache, expFeatures)));

    // Only need content type if there is no list information
    // or list is not odb but no contentTypes in cache.
    // or contentTypeKeysFacet is not resolved.
    let needsContentTypes = false;
    let needsListAvailableContentTypes = false;
    const isDocumentSetsModernViewEnabled = isFeatureEnabled(DocumentSetsModernView) && isFeatureEnabled(UseItemsScope);
    const needsContentTypeOrder = isDocumentSetsModernViewEnabled;
    if (!listFacets ||
      (listFacets && listFacets.templateType !== ListTemplateType.mySiteDocumentLibrary && (
        !contentTypeKeys || contentTypeKeys.length < 1 ||
        !isContentTypeKeysResolved
      ))) {
      if (isDocumentSetsModernViewEnabled) {
        needsListAvailableContentTypes = true;
      } else {
        needsContentTypes = true;
      }
    }

    let needsGridInitInfo = false;
    // when needsViewMetadata is true means the current view data doesn't exist or view is invalidate,
    // we should as for gridInitInfo as well.
    if (isFeatureEnabled({ ODB: 1231 /** QuickEditTypeScript **/ }) &&
      (!gridInitInfo || needsViewMetadata || !isGridInitInfoResolved) &&
      (!listFacets || (listFacets && listFacets.templateType !== ListTemplateType.mySiteDocumentLibrary))
    ) {
      needsGridInitInfo = true;
    }

    let needUpdatePageContext = false;
    let allowMultipleValueFilterForTaxonomyFields = true;
    let needsSpotlight = !isSpotlightResolved; // only need to fetch if spotlight is not resolved means changed
    let viewIdMismatch = false;

    const {
      pageContext: spPageContext
    } = spPageContextFacets || {
      pageContext: undefined
    };

    if (!spPageContext || !spPageContext.listId) {
      needUpdatePageContext = true;
    } else {
      const itemUrlHelper = new ItemUrlHelperBasic({
        context: {
          webAbsoluteUrl: spPageContext.webAbsoluteUrl,
          listUrl: spPageContext.listUrl
        }
      });
      const pageContextListParts = itemUrlHelper.getUrlParts({
        listUrl: spPageContext.listUrl
      });
      if (itemKey.listFullUrl !== pageContextListParts.fullListUrl) {
        needUpdatePageContext = true;
      }
    }

    if (!needUpdatePageContext && spPageContext) {
      if (itemSetKey.viewKey && itemSetKey.viewKey.viewId !== Guid.normalizeLower(spPageContext.viewId, false/*strip brackets*/)) {
        viewIdMismatch = true;
      }

      if (!spPageContext.isSPO) {
        // AllowMultipleValueFilterForTaxonomyFields is not available in on-prem yet, so we shouldn't send it to the server at all.
        allowMultipleValueFilterForTaxonomyFields = false;
      }

      needsSpotlight = needsSpotlight && shouldFetchSpotlight({ listBaseTemplate: spPageContext.listBaseTemplate, pageToken: pageToken });
    }

    const {
      spClientSideFrameworkConfiguration,
      localeConfiguration
    } = dispatch(readConfiguration({
      spClientSideFrameworkConfiguration: spClientSideFrameworkConfigurationFacet,
      localeConfiguration: localeFacet
    }));

    // TODO need to only request the schema when we need it.
    // Ensure we don't ask for schema again when getting next page.
    // Otherwise the unsaved column schema change will be overrided, such as preview column formatter.

    // TODO: we need to refactor processResponse to ensure when we don't ask schema from server, we can
    // still process item data correctly.
    // Currently for custom list and grouped list, we have some special logic depends on server return schema.
    const needsSchema = true; // !pageToken;
    const needsSpfxFrameworkInfo = (spClientSideFrameworkConfiguration &&
      spClientSideFrameworkConfiguration.requestSpfxFrameworkInfo) || viewIdMismatch;
    getItemContext.postDataContext = {
      needUpdatePageContext: needUpdatePageContext,
      allowMultipleValueFilterForTaxonomyFields: allowMultipleValueFilterForTaxonomyFields,
      needsSpotlight: needsSpotlight,
      needsViewMetadata: needsViewMetadata,
      needsContentTypes: needsContentTypes,
      needsListAvailableContentTypes: needsListAvailableContentTypes,
      needsContentTypeOrder: needsContentTypeOrder,
      needsSchema: needsSchema,
      needClientSideComponentManifest: needsSpfxFrameworkInfo,
      needsGridInitInfo: needsGridInitInfo
    };
    getItemContext.disableGrouping = disableGrouping;
    getItemContext.preferredLanguage = localeConfiguration && localeConfiguration.language;

    return getItemContext;
  };
}

export function constructGetItemContext(
  itemKey: typeof spItemKeyFacet.type,
  contentTypes: ISPContentType[] | undefined,
  qosName: string
): IItemCacheThunkAction<ISPGetItemContext> {
  return (dispatch: IItemCacheDispatch) => {
    const {
      webAbsoluteUrl,
      listFullUrl
    } = itemKey;

    const spPageContextFacets = dispatch(readItem({
      pageContext: spPageContextFacet
    }, spPageContextKeyFacet.serialize({
      webAbsoluteUrl: webAbsoluteUrl,
      listFullUrl: listFullUrl
    })));

    let itemIds: string[] | undefined;
    let itemRefs: string[] | undefined;
    let contentTypeFields = ['File_x0020_Type', 'FileRef', 'FileLeafRef'];

    let rootFolder: string | undefined;

    if (itemKey.itemId) {
      itemIds = [itemKey.itemId];
    } else if (itemKey.rootFolder) {
      const parentFolderPath = itemKey.rootFolder;
      const itemUrlHelper = new ItemUrlHelperBasic({
        context: {
          webAbsoluteUrl: itemKey.webAbsoluteUrl,
          listUrl: itemKey.listFullUrl
        }
      });
      const itemUrlParts = itemUrlHelper.getUrlParts({
        path: parentFolderPath
      });

      const {
        serverRelativeItemUrl
      } = itemUrlParts;

      itemRefs = serverRelativeItemUrl ? [serverRelativeItemUrl] : [];
    } else {
      rootFolder = new SimpleUri(itemKey.listFullUrl).path;
      itemRefs = [rootFolder];
    }

    const itemFacets = dispatch(readItem({
      contentTypeId: contentTypeIdFacet
    }, spItemKeyFacet.serialize(itemKey)));

    // TODO: listContentTypes returned from server doesn't include folderContentType.
    // We will have server side change to return foldeContentType specifically,
    // we will need to modify the following logic to make sure add folder contentType fields
    // for folder item.
    if (contentTypes && contentTypes.length > 0 && itemFacets && itemFacets.contentTypeId) {
      for (let contentType of contentTypes) {
        if (contentType.contentTypeId === itemFacets.contentTypeId && contentType.Fields) {
          let fields = contentType.Fields.split(',');
          for (let fieldName of fields) {
            if (fieldName === 'Modified_x0020_By') {
              contentTypeFields.push('Editor');
            } else if (fieldName === 'Created_x0020_By') {
              contentTypeFields.push('Author');
            } else {
              contentTypeFields.push(fieldName);
            }
          }
        }
      }
    }

    let viewXml = getViewXml({
      itemIds: itemIds,
      itemRefs: itemRefs,
      recurseInFolders: true,
      pageSize: 1,
      fieldNames: contentTypeFields
    });

    return {
      webAbsoluteUrl: itemKey.webAbsoluteUrl,
      listFullUrl: itemKey.listFullUrl,
      currentPageContext: spPageContextFacets && spPageContextFacets.pageContext,
      qosName: qosName,
      viewXml: viewXml,
      rootFolder: rootFolder,
      postDataContext: {
        needsSchema: true
      }
    };
  };
}

export function getContentTypes(payload: {
  itemSetKey: typeof spFolderItemSetKeyFacet.type;
}): IItemCacheThunkAction<ISPContentType[] | undefined> {
  const {
    itemSetKey
  } = payload;

  return (dispatch: IItemCacheDispatch) => {
    const listKey = spListKeyFacet.serialize({
      webAbsoluteUrl: itemSetKey.webAbsoluteUrl,
      listFullUrl: itemSetKey.listFullUrl
    });
    const listContentTypeFacets = dispatch(readItem({
      contentTypeKeys: contentTypeKeysFacet
    }, listKey));

    const contentTypeKeys = listContentTypeFacets && listContentTypeFacets.contentTypeKeys;
    if (contentTypeKeys && contentTypeKeys.length) {
      const contentTypes: ISPContentType[] = [];
      contentTypeKeys.forEach((contentTypeKey: string) => {
        const contentTypeFacets = dispatch(readItem({
          spListContentType: spListContentTypeFacet
        }, contentTypeKey));
        if (contentTypeFacets && contentTypeFacets.spListContentType) {
          contentTypes.push(contentTypeFacets.spListContentType);
        }
      });
      return contentTypes;
    }
  };
}

function getIsFeatureEnabled(itemCache: IItemCache, expFeatures: number[]): (feature: IFeature) => boolean {
  const isExpFeatureEnabled = getFeatureChecker(itemCache);
  return (feature: IFeature) => { return isExpFeatureEnabled(feature, expFeatures); };
}

function shouldFetchSpotlight(params: {
  pageToken: string | undefined,
  listBaseTemplate: ListTemplateType | undefined
}): boolean {
  const {
    listBaseTemplate,
    pageToken
  } = params;
  // Retrieving spotlight information costs an extra sql roundtrip, so ideally we don't want to do it every time.
  // We don't want to do it on a request to get the second+ page, so if a requestToken is set, we won't retrieve
  // spotlight info.
  if (pageToken) {
    return false;
  }

  let needsSpotlight = true;
  if (listBaseTemplate) {
    // custom lists don't use Spotlight
    needsSpotlight = isDocumentLibrary(Number(listBaseTemplate));
  }

  return needsSpotlight;
}

// If filterList contains today format filter or use lookup id filter,
// we need to convert filters to caml before make request to server.
function shouldConvertFiltersToCaml(filterList: IFilter[]): boolean {
  if (filterList) {
    for (let filter of filterList) {
      if (filter.lookupId) {
        return true;
      }

      const lowerCaseType = filter.type && filter.type.toLowerCase();
      if (lowerCaseType === 'datetime' && filter.values) {
        for (let filterValue of filter.values) {
          if (filterValue && CamlUtilities.isTodayString(filterValue)) {
            return true;
          }
        }
      }
    }
  }

  return false;
}
